import React, { useState, useRef } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Typography,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateNext,
  NavigateBefore,
  OpenInNew
} from '@mui/icons-material';
import { StepperData } from '../types/documentation';

interface StepperDocumentationProps {
  data: StepperData;
  onClose: () => void;
}

const StepperDocumentation: React.FC<StepperDocumentationProps> = ({ data, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const contentBoxRef = useRef<HTMLDivElement>(null);

  // Initialize refs array
  React.useEffect(() => {
    stepRefs.current = stepRefs.current.slice(0, data.steps.length);
  }, [data.steps.length]);

  const scrollToStep = (stepIndex: number, offset: number = 80) => {
    // Use requestAnimationFrame to wait for the next paint
    requestAnimationFrame(() => {
      setTimeout(() => {
        const stepElement = stepRefs.current[stepIndex];
        const container = contentBoxRef.current;
        
        if (stepElement && container) {
          // Get the position of the step element relative to the document
          const stepTop = stepElement.offsetTop;
          
          // Scroll to position with custom offset
          container.scrollTo({
            top: stepTop - offset,
            behavior: 'smooth'
          });
        }
      }, 300); // Wait for stepper expand/collapse animation
    });
  };

  const handleNext = () => {
    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
    scrollToStep(nextStep, 110);
  };

  const handleBack = () => {
    const prevStep = activeStep - 1;
    setActiveStep(prevStep);
    scrollToStep(prevStep, 80);
  };

  const handleReset = () => {
    setActiveStep(0);
    scrollToStep(0);
  };

  const renderStepContent = (content: any) => {
    if (typeof content === 'string') {
      return <Typography variant="body2">{content}</Typography>;
    }

    if (Array.isArray(content)) {
      return (
        <Box>
          {content.map((item, index) => (
            <Typography key={index} variant="body2" sx={{ mb: 1 }}>
              {item}
            </Typography>
          ))}
        </Box>
      );
    }

    // If content has structured data
    if (content.description) {
      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {content.description}
          </Typography>
          {content.steps && (
            <Box component="ol" sx={{ pl: 2 }}>
              {content.steps.map((step: string, index: number) => (
                <Typography component="li" key={index} variant="body2" sx={{ mb: 1 }}>
                  {step}
                </Typography>
              ))}
            </Box>
          )}
          {content.code && (
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                overflowX: 'auto'
              }}
            >
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{content.code}</pre>
            </Paper>
          )}
          {content.image && (
            <Box
              component="img"
              src={content.image}
              alt={content.imageAlt || 'Step illustration'}
              sx={{
                mt: 2,
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            />
          )}
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 64,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#fff',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {data.title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Main Content - Scrollable */}
      <Box
        ref={contentBoxRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#fafafa',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#a8a8a8',
            },
          },
        }}
      >
        <Box
          sx={{
            width: '90%',
            // maxWidth: 900,
            p: 3,
            textAlign: 'left'
          }}
        >
          {/* Description */}
          {data.description && (
            <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
              {data.description}
            </Typography>
          )}

          {/* Stepper */}
          <Stepper activeStep={activeStep} orientation="vertical">
            {data.steps.map((step, index) => (
              <Step key={index}>
                <Box 
                  ref={(el: HTMLDivElement | null) => { stepRefs.current[index] = el; }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                >
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontSize: '1.1rem',
                        fontWeight: 600
                      }
                    }}
                  >
                    {step.label}
                  </StepLabel>
                  {typeof step.content === 'object' && !Array.isArray(step.content) && (step.content as any).link && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      endIcon={<OpenInNew />}
                      onClick={() => window.open((step.content as any).link, '_blank', 'noopener,noreferrer')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      {(step.content as any).linkText || 'Open Link'}
                    </Button>
                  )}
                </Box>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    {renderStepContent(step.content)}
                  </Box>
                  <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ mt: 1 }}
                      endIcon={<NavigateNext />}
                      disabled={index === data.steps.length - 1}
                    >
                      {index === data.steps.length - 1 ? 'Finish' : 'Next'}
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mt: 1 }}
                      startIcon={<NavigateBefore />}
                    >
                      Back
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {/* Completion Message */}
          {activeStep === data.steps.length && (
            <Paper square elevation={0} sx={{ p: 3, mt: 2, backgroundColor: '#e3f2fd' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                🎉 All steps completed!
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {data.completionMessage || 'You have successfully completed the setup process.'}
              </Typography>
              <Button onClick={handleReset} sx={{ mt: 1 }} variant="outlined">
                Reset Steps
              </Button>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Fixed Footer with Navigation - Only show if stepper is active */}
      {activeStep < data.steps.length && (
        <Box
          sx={{
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fff',
            px: 3,
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            bottom: 0,
            zIndex: 1
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Step {activeStep + 1} of {data.steps.length}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<NavigateBefore />}
              variant="outlined"
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<NavigateNext />}
            >
              {activeStep === data.steps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StepperDocumentation;
